import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Boxes, CloudUpload, Filter, Gauge, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useProject } from "@/pages/project/ProjectLayout";
import { useProjectSocket } from "@/hooks/useProjectSocket";
import { useViewer } from "@/components/viewer/useViewer";
import { ViewerToolbar, ViewerOptionsBar } from "@/components/viewer/ViewerToolbar";
import { ModelTree } from "@/components/viewer/ModelTree";
import { ModelUploadDialog } from "@/components/viewer/ModelUploadDialog";
import { ViewerStatsPanel } from "@/components/viewer/ViewerStatsPanel";
import { StatusLegend } from "@/components/viewer/StatusLegend";
import { PanelProperties } from "@/components/panel/PanelProperties";
import { mergeSelectedPanels } from "@/components/panel/mergePanels";
import { IssueMarkerPopup } from "@/components/issues/IssueMarkerPopup";
import { FilterBar } from "@/components/table/FilterBar";
import { IssueDialog } from "@/components/issues/IssueDialog";
import { BulkActionsBar } from "@/components/panel/BulkActionsBar";
import { PresetEditDialog } from "@/components/model/PresetEditDialog";
import { isPanelBlock } from "@/lib/anchorNames";
import { buildCompareColorMap, COMPARE_COLORS } from "@/components/viewer/RevisionCompare";
import { useAuth } from "@/context/AuthContext";
import { issueMarkerColor, ISSUE_TYPE_COLORS } from "@/constants/workflow";

export default function ModelPage() {
  const { project, projectId, reload } = useProject();
  const { capabilities, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const linkedUids = location.state?.uids;
  const containerRef = useRef(null);

  const [panels, setPanels] = useState([]);
  const panelVersions = useMemo(
    () => Object.fromEntries(panels.map((p) => [p.panel_uid, p.updated_at])),
    [panels]
  );
  const [pallets, setPallets] = useState([]);
  const [options, setOptions] = useState({});
  const [filters, setFilters] = useState({});
  const [selection, setSelection] = useState([]);
  const [pendingSelect, setPendingSelect] = useState(null);
  const [activePanel, setActivePanel] = useState(null);
  const [colorMode, setColorMode] = useState("original");
  const [mode, setMode] = useState("select");
  const [section, setSection] = useState({ axis: null, value: 1 });
  const [treeOpen, setTreeOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [issuePanel, setIssuePanel] = useState(null);
  const [hiddenLayers, setHiddenLayers] = useState(new Set());
  const [measure, setMeasure] = useState(null);
  const [search, setSearch] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [compareResult, setCompareResult] = useState(null);
  const [editPreset, setEditPreset] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const { isCompact } = useIsMobile();
  const [mobileSheet, setMobileSheet] = useState(null);
  const [markerPanel, setMarkerPanel] = useState(null);
  const panelIssueSet = useRef(new Set());
  const selectionEpoch = useRef(0);
  // Filters/colour mode applied by an "Action Required" deep link, plus what to restore.
  const deepFilterRef = useRef(null);
  // Set after the viewer exists so handleSelect can reach the shared clear routine.
  const clearRef = useRef(null);
  const handledLinkRef = useRef(null);
  const [presets, setPresets] = useState([]);
  const [canManagePresets, setCanManagePresets] = useState(false);

  const loadPresets = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/projects/${projectId}/presets`);
      let list = res.presets || [];
      setCanManagePresets(!!res.can_manage);
      // One-off migration of the old browser-only presets.
      const legacy = JSON.parse(localStorage.getItem(`okn-presets-${projectId}`) || "[]");
      if (res.can_manage && legacy.length && list.length === 0) {
        for (const item of legacy) {
          await api.post(`/projects/${projectId}/presets`, {
            name: item.name,
            filters: item.filters || {},
          });
        }
        localStorage.removeItem(`okn-presets-${projectId}`);
        list = (await api.get(`/projects/${projectId}/presets`)).data.presets || [];
      }
      setPresets(list);
    } catch (error) {
      console.debug("presets unavailable", error);
    }
  }, [projectId]);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  const savePreset = async () => {
    const active = Object.entries(filters).filter(([, value]) => value);
    if (!active.length && !selection.length) {
      toast.error("Apply a filter or select panels before saving a preset");
      return;
    }
    const name = window.prompt("Preset name");
    if (!name?.trim()) return;
    try {
      await api.post(`/projects/${projectId}/presets`, {
        name: name.trim(),
        // A manual selection wins: the preset then stores exactly those panels.
        filters: selection.length ? {} : Object.fromEntries(active),
        panel_uids: selection.length ? selection : null,
      });
      toast.success(`Preset "${name.trim()}" saved`);
      loadPresets();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const deletePreset = async (name) => {
    const preset = presets.find((p) => p.name === name);
    if (!preset) return;
    try {
      await api.delete(`/projects/${projectId}/presets/${preset.id}`);
      loadPresets();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  useEffect(() => {
    if (selection.length > 1) setBulkOpen(true);
    if (selection.length === 0) setBulkOpen(false);
  }, [selection.length]);

  const compareColors = useMemo(
    () => buildCompareColorMap(compareResult),
    [compareResult]
  );

  const revisionId = project?.active_revision_id;

  const query = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    return params.toString();
  }, [filters]);

  const loadPanels = useCallback(async () => {
    if (!revisionId) return;
    try {
      const { data } = await api.get(`/projects/${projectId}/panels?${query}`);
      setPanels(data.panels);
    } catch (error) {
      toast.error(apiError(error));
    }
  }, [projectId, revisionId, query]);

  const loadSupport = useCallback(async () => {
    if (!revisionId) return;
    try {
      const [p, o] = await Promise.all([
        api.get(`/projects/${projectId}/pallets`),
        api.get(`/projects/${projectId}/panels/filter-options`),
      ]);
      setPallets(p.data);
      setOptions(o.data);
    } catch (error) {
      console.warn("[model] support data (pallets/filter options) failed to load", error);
    }
  }, [projectId, revisionId]);

  useEffect(() => {
    loadPanels();
  }, [loadPanels]);
  useEffect(() => {
    loadSupport();
  }, [loadSupport]);

  const refreshPanel = useCallback(
    async (uid) => {
      const target = uid || activePanel?.panel_uid;
      if (!target) return;
      const epoch = selectionEpoch.current;
      try {
        const { data } = await api.get(`/projects/${projectId}/panels/${target}`);
        if (selectionEpoch.current !== epoch) return; // selection was cleared meanwhile
        setActivePanel(data);
        setPanels((prev) =>
          prev.map((panel) => (panel.panel_uid === target ? { ...panel, ...data } : panel))
        );
      } catch (error) {
        console.debug("[model] panel refresh skipped (may be filtered out)", error);
      }
    },
    [projectId, activePanel?.panel_uid]
  );

  const handleSelect = useCallback(
    (uids) => {
      setSelection(uids);
      if (uids.length === 1) {
        refreshPanel(uids[0]);
        // Auto-open the issue popup when the selected panel has issues.
        setMarkerPanel(
          panelIssueSet.current.has(uids[0]) ? uids[0] : null
        );
      } else if (uids.length === 0) {
        // Clicking empty space clears everything the selection brought with it.
        clearRef.current?.();
      }
    },
    [refreshPanel]
  );

  const { viewer, ready, progress, manifest, loadTelemetry, loadError, detailMode, detailProgress, detailAvailable, loadDetail, backToLight, clearDetailSelection } = useViewer({
    containerRef,
    projectId,
    revisionId,
    panels,
    colorMode,
    compareColors,
    onSelect: handleSelect,
    enabled: !!project,
  });

  const [reprocessing, setReprocessing] = useState(false);
  const [markerNonce, setMarkerNonce] = useState(0);
  const [recentIssues, setRecentIssues] = useState([]);
  const [labelTargets, setLabelTargets] = useState({ panels: true, anchors: true });
  const labelsOn = labelTargets.panels || labelTargets.anchors;

  // Multi-selection: show shared property values and "Varies" where panels differ.
  const sidePanel = useMemo(() => {
    if (selection.length > 1) {
      const chosen = new Set(selection);
      return mergeSelectedPanels(panels.filter((panel) => chosen.has(panel.panel_uid)));
    }
    return activePanel;
  }, [selection, panels, activePanel]);

  useEffect(() => {
    if (!viewer.current || !ready) return;
    // Labels only exist for panel- and anchor-classified blocks, each toggled
    // independently from the toolbar.
    viewer.current.setPanelLabels(
      panels
        .filter((panel) =>
          panel.is_anchor ? labelTargets.anchors : labelTargets.panels && isPanelBlock(panel.block_definition)
        )
        .map((panel) => ({
          uid: panel.panel_uid,
          text: panel.block_definition || panel.type_code,
          subtext: panel.unique_code || null,
        }))
    );
  }, [labelTargets, panels, ready, viewer]);

  const reprocessModel = useCallback(async () => {
    setReprocessing(true);
    try {
      await api.post(`/projects/${projectId}/models/${revisionId}/reprocess`);
      toast.success("Re-processing started — the viewer package is being rebuilt");
      const deadline = Date.now() + 180000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        const res = await api.get(`/projects/${projectId}/models`);
        const row = (res.data || []).find((m) => m.id === revisionId);
        if (row?.processing_status === "processed") {
          window.location.reload();
          return;
        }
        if (row?.processing_status === "failed") {
          toast.error(row.processing_error || "Processing failed");
          break;
        }
      }
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setReprocessing(false);
    }
  }, [projectId, revisionId]);

  const loadPercent = progress
    ? Math.min(99, Math.round((progress.loaded / (progress.total || 1)) * 100))
    : 5;

  let loadStageLabel = "Loading model";
  if (progress) loadStageLabel = progress.stage === "geometry" ? "Loading geometry" : "Loading panels";

  const showBulkBar =
    selection.length > 0 &&
    bulkOpen &&
    !capabilities?.is_guest &&
    (!isCompact || mobileSheet === "bulk");

  useProjectSocket(projectId, (message) => {
    if (["comments_purged", "issues_purged"].includes(message.event)) {
      setMarkerNonce((n) => n + 1);
      if (activePanel?.panel_uid) refreshPanel(activePanel.panel_uid);
    }
    if (message.event === "panel_status_changed") {
      const { panel_uid: uid, statuses, workflow_blocked, blocked_by, block_reason } = message.payload;
      setPanels((prev) =>
        prev.map((panel) =>
          panel.panel_uid === uid
            ? { ...panel, statuses, workflow_blocked, blocked_by, block_reason }
            : panel
        )
      );
      if (activePanel?.panel_uid === uid) refreshPanel(uid);
      toast.info(`${message.payload.panel_name}: ${message.payload.department} updated`, {
        description: `by ${message.payload.updated_by}`,
      });
    }
    if (["pallet_assigned", "panel_glass_changed"].includes(message.event)) loadPanels();
    if (message.event === "revision_activated") reload();
  });

  useEffect(() => {
    if (!viewer.current) return;
    viewer.current.setMode(mode);
    viewer.current.onMeasure = setMeasure;
  }, [mode, viewer]);

  useEffect(() => {
    if (!viewer.current || !ready) return;
    viewer.current.setSection(section.axis, section.value);
  }, [section, ready, viewer]);

  useEffect(() => {
    if (!viewer.current || !ready) return;
    viewer.current.onIssueMarkerClick = (panelUid) => {
      setMarkerPanel(panelUid);
      setSelection([panelUid]);
      viewer.current.setSelection([panelUid], false);
      refreshPanel(panelUid);
    };
    const withIssues = panels.filter((panel) => (panel.open_issues || 0) > 0);
    panelIssueSet.current = new Set(withIssues.map((panel) => panel.panel_uid));
    viewer.current.setIssueMarkers(
      withIssues.map((panel) => ({
        uid: panel.panel_uid,
        color: issueMarkerColor(panel.open_issue_types),
        recent: recentIssues.includes(panel.panel_uid),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panels, ready, viewer, recentIssues]);

  useEffect(() => {
    if (!viewer.current || !ready || !revisionId) return;
    let cancelled = false;
    api
      .get(`/projects/${projectId}/panel-markers`)
      .then(({ data }) => {
        if (cancelled || !viewer.current) return;
        viewer.current.setNoteMarkers(data.notes);
        setRecentIssues(data.recent_issues || []);
        viewer.current.setNewMarkers(data.added);
        if (data.summary && !data.summary_seen) {
          const { added: a = 0, removed: r = 0, renamed: rn = 0 } = data.summary;
          if (a || r || rn) {
            toast.info("Model updated", {
              description: `${a} panels added · ${r} removed · ${rn} renamed`,
              duration: 10000,
            });
          }
          api
            .post(`/projects/${projectId}/panel-markers/seen`, {
              revision_id: data.revision_id,
              panel_uids: ["__revision_summary__"],
            })
            .catch(() => {});
        }
        viewer.current.onNewBadgeSeen = (uid) =>
          api
            .post(`/projects/${projectId}/panel-markers/seen`, {
              revision_id: data.revision_id,
              panel_uids: [uid],
            })
            .catch(() => {});
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId, revisionId, ready, panels.length, markerNonce, viewer]);

  // Deep links from the Overview "Action Required" cards: ?status_design=Revision%20Required&select=1
  const deepLink = searchParams.toString();
  useEffect(() => {
    if (!deepLink) {
      handledLinkRef.current = null; // allow the same card to be clicked again later
      return;
    }
    if (handledLinkRef.current === deepLink) return;
    handledLinkRef.current = deepLink;
    const incoming = {};
    let colour = null;
    searchParams.forEach((value, key) => {
      if (key === "select") return;
      incoming[key] = value;
      if (key.startsWith("status_")) colour = key.replace("status_", "");
    });
    if (Object.keys(incoming).length) {
      setFilters((prev) => {
        // Snapshot only on the first pass so a re-run never records the temporary filters.
        if (!deepFilterRef.current)
          deepFilterRef.current = { prevFilters: prev, prevColorMode: null };
        return incoming;
      });
      if (colour)
        setColorMode((prevMode) => {
          if (deepFilterRef.current && !deepFilterRef.current.prevColorMode)
            deepFilterRef.current.prevColorMode = prevMode;
          return colour;
        });
      if (searchParams.get("select") === "1") {
        const expected = new URLSearchParams();
        Object.entries(incoming).forEach(([key, value]) => expected.set(key, value));
        api
          .get(`/projects/${projectId}/panels?${expected.toString()}`)
          .then(({ data }) => setPendingSelect(data.panels.map((p) => p.panel_uid)))
          .catch(() => {});
      } else if (linkedUids?.length) {
        // Explicit panel list handed over by the caller (e.g. an Overview status reason).
        setPendingSelect(linkedUids);
      }
    } else if (searchParams.get("select") === "1" && linkedUids?.length) {
      // BOM order "Show in Model": explicit uid list, no filters to apply.
      setPendingSelect(linkedUids);
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);


  /** Single place that cancels a selection: highlights, panels, sheets and deep-link filters. */
  const clearSelection = useCallback(() => {
    selectionEpoch.current += 1;
    setPendingSelect(null);
    setSelection([]);
    setActivePanel(null);
    setMarkerPanel(null);
    setBulkOpen(false);
    setMobileSheet(null);
    viewer.current?.setSelection([], false);
    const ctx = deepFilterRef.current;
    if (ctx) {
      deepFilterRef.current = null;
      setFilters(ctx.prevFilters || {});
      if (ctx.prevColorMode) setColorMode(ctx.prevColorMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewer]);

  useEffect(() => {
    clearRef.current = clearSelection;
  }, [clearSelection]);

  const selectFiltered = useCallback(() => {
    const uids = panels.map((panel) => panel.panel_uid);
    if (uids.length === 0) {
      toast.info("No panels match the current filters");
      return;
    }
    setSelection(uids);
    viewer.current?.setSelection(uids, false);
    setBulkOpen(true);
    if (uids.length === 1) refreshPanel(uids[0]);
    toast.success(`${uids.length} panels selected`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panels, refreshPanel]);

  useEffect(() => {
    // Select the deep-linked panels once the viewer is ready.
    if (!pendingSelect?.length || !ready) return;
    setSelection(pendingSelect);
    viewer.current?.setSelection(pendingSelect, false);
    const uids = pendingSelect;
    // Give the streamed chunks a moment so every selected panel has geometry to frame.
    setTimeout(() => viewer.current?.fitToView(uids), 900);
    setBulkOpen(true);
    toast.success(`${pendingSelect.length} panels selected`);
    setPendingSelect(null);
  }, [pendingSelect, ready, viewer]);


  const selectByStatus = useCallback(
    (statusKey) => {
      const value = statusKey === "__null__" ? null : statusKey;
      const uids = panels
        .filter((panel) => (panel.statuses?.[colorMode] ?? null) === value)
        .map((panel) => panel.panel_uid);
      if (!uids.length) return;
      setSelection(uids);
      viewer.current?.setSelection(uids, false);
      if (uids.length === 1) refreshPanel(uids[0]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [panels, colorMode, refreshPanel]
  );

  useEffect(() => {
    const handler = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "a") {
        const tag = document.activeElement?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        event.preventDefault();
        selectFiltered();
      }
      if (event.key === "Escape") {
        const tag = document.activeElement?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        clearSelection();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectFiltered, clearSelection]);

  const toggleLayer = (path) => {
    const next = new Set(hiddenLayers);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    setHiddenLayers(next);
    viewer.current?.setLayerVisible(path, !next.has(path));
  };

  const canUpload =
    !capabilities?.is_guest && (project?.can_manage_models ?? capabilities?.is_admin);

  if (!project) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="model-page">
      {isCompact ? (
        <div className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 py-2">
          <button
            data-testid="mobile-filters-button"
            onClick={() => setMobileSheet(mobileSheet === "filters" ? null : "filters")}
            className="mono flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-700"
          >
            <Filter size={12} /> Filters
          </button>
          <span className="mono whitespace-nowrap text-[10px] uppercase tracking-wider text-slate-500">
            {panels.filter((p) => !p.is_anchor).length} panels
          </span>
          {selection.length ? (
            <button
              data-testid="mobile-selection-button"
              onClick={() => {
                setBulkOpen(true);
                setMobileSheet("bulk");
              }}
              className="mono ml-auto shrink-0 rounded-full bg-cyan-600 px-3 py-1.5 text-[10px] uppercase tracking-wider text-white"
            >
              Selection ({selection.length})
            </button>
          ) : null}
          {activePanel ? (
            <button
              data-testid="mobile-panel-info-button"
              onClick={() => setMobileSheet(mobileSheet === "panel" ? null : "panel")}
              className={`mono shrink-0 rounded-full border border-slate-200 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-700 ${
                selection.length ? "" : "ml-auto"
              }`}
            >
              Panel info
            </button>
          ) : null}
        </div>
      ) : null}

      {!isCompact || mobileSheet === "filters" ? (
        <div className={isCompact ? "okn-scroll max-h-[45vh] overflow-y-auto" : ""}>
          <FilterBar
            filters={filters}
            setFilters={setFilters}
            options={options}
            onReset={() => setFilters({})}
            onSelectFiltered={selectFiltered}
            matchCount={panels.filter((p) => !p.is_anchor).length}
            presets={presets}
            onSavePreset={savePreset}
            selectionCount={selection.length}
            onApplyPreset={(preset) => {
              // Manual presets carry their own panel list; filter presets restore the filters.
              if (preset.panel_uids?.length) {
                setFilters({});
                setPendingSelect(preset.panel_uids);
              } else {
                setFilters(preset.filters);
              }
            }}
            onDeletePreset={deletePreset}
            onEditPreset={setEditPreset}
            canManagePresets={canManagePresets}
          />
        </div>
      ) : null}

      {revisionId ? (
        <ViewerOptionsBar
          labelsOn={labelsOn}
          labelTargets={labelTargets}
          onLabelTarget={(key) =>
            setLabelTargets((t) => ({ ...t, [key]: !t[key] }))
          }
          detailMode={detailMode}
          detailProgress={detailProgress}
          detailAvailable={detailAvailable}
          onToggleDetail={() =>
            detailMode === "full" ? backToLight() : loadDetail(null)
          }
          onDetailSelection={() => selection.length && loadDetail(selection)}
          onClearDetail={clearDetailSelection}
          selectionCount={selection.length}
        />
      ) : null}

      <div className="flex min-h-0 flex-1">
        {treeOpen ? (
          <ModelTree
            panels={panels}
            layers={manifest?.layers || []}
            selection={selection}
            onSelect={(uids) => {
              setSelection(uids);
              viewer.current?.setSelection(uids, false);
              refreshPanel(uids[0]);
              viewer.current?.fitToView(uids);
            }}
            hiddenLayers={hiddenLayers}
            toggleLayer={toggleLayer}
            layersDisabled={detailMode === "light"}
          />
        ) : null}

        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="relative min-h-0 flex-1">
            <div ref={containerRef} className="absolute inset-0" data-testid="viewer-canvas" />

            {!revisionId ? (
              <div className="absolute inset-0 grid place-items-center px-6" data-testid="no-model-state">
                <div className="max-w-sm rounded-2xl border border-dashed border-slate-200 p-10 text-center">
                  <Boxes className="mx-auto text-slate-500" />
                  <p className="mt-4 text-sm text-slate-600">
                    No active model revision. Upload a Rhino .3dm file to begin.
                  </p>
                  {canUpload ? (
                    <button
                      data-testid="upload-model-empty-button"
                      onClick={() => setUploadOpen(true)}
                      className="mono mt-5 rounded-full bg-cyan-500 px-5 py-2 text-[11px] uppercase tracking-wider text-[#04121a]"
                    >
                      Upload model
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {revisionId && loadError ? (
              <div
                data-testid="viewer-load-error"
                className="absolute inset-0 z-20 grid place-items-center bg-[#EEF2F8]/90 backdrop-blur-sm"
              >
                <div className="w-96 rounded-2xl border border-red-200 bg-white px-7 py-6 text-center shadow-xl">
                  <p className="mono text-[11px] uppercase tracking-[0.2em] text-red-600">
                    Model could not be loaded
                  </p>
                  <p className="mt-3 text-[12.5px] leading-relaxed text-slate-600">
                    The optimised viewer package for this revision is missing on the server.
                    Re-processing rebuilds it from the uploaded .3dm file.
                  </p>
                  <button
                    data-testid="reprocess-model-button"
                    disabled={reprocessing}
                    onClick={reprocessModel}
                    className="mono mt-5 rounded-full bg-cyan-500 px-5 py-2 text-[11px] uppercase tracking-wider text-[#04121a] disabled:opacity-60"
                  >
                    {reprocessing ? "Re-processing…" : "Re-process model"}
                  </button>
                </div>
              </div>
            ) : null}

            {revisionId && !ready && !loadError ? (
              <div
                data-testid="viewer-loading"
                className="absolute inset-0 z-10 grid place-items-center bg-[#EEF2F8]/80 backdrop-blur-sm"
              >
                <div className="w-80 rounded-2xl border border-slate-200 bg-white/95 px-7 py-6 text-center shadow-xl">
                  <Loader2 size={26} className="mx-auto animate-spin text-cyan-600" />
                  <p className="mono mt-4 text-[11px] uppercase tracking-[0.2em] text-cyan-700">
                    {loadStageLabel}
                  </p>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full bg-cyan-500 transition-[width]"
                      style={{ width: `${loadPercent}%` }}
                    />
                  </div>
                  <p
                    data-testid="viewer-loading-percent"
                    className="mono mt-2 text-[11px] text-slate-600"
                  >
                    {loadPercent}%
                    {progress ? ` · ${progress.loaded} / ${progress.total || "?"}` : ""}
                  </p>
                  {!progress ? (
                    <p className="mt-2 text-[11px] text-slate-500">Initialising 3D viewer…</p>
                  ) : null}
                </div>
              </div>
            ) : null}

            {revisionId ? (
              <div
                data-testid="issue-marker-legend"
                className="pointer-events-none absolute bottom-12 left-4 z-20 hidden rounded-xl border border-slate-300 bg-white/95 px-3 py-2 shadow-lg backdrop-blur-md lg:block"
              >
                <p className="mono mb-1.5 text-[9px] uppercase tracking-[0.18em] text-slate-500">
                  Issue markers
                </p>
                <div className="space-y-0.5">
                  {Object.entries(ISSUE_TYPE_COLORS).map(([type, color]) => (
                    <div key={type} className="flex items-center gap-2">
                      <span
                        className="grid h-3 w-3 place-items-center rounded-full text-[7px] font-bold text-white"
                        style={{ backgroundColor: color }}
                      >
                        !
                      </span>
                      <span className="text-[10.5px] text-slate-600">{type}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {revisionId ? (
              <ViewerToolbar
                mode={mode}
                setMode={setMode}
                onFit={() => viewer.current?.fitToView(selection.length ? selection : null)}
                onHome={() => viewer.current?.homeView()}
                onIsolate={() => selection.length && viewer.current?.isolate(selection)}
                onHide={() => selection.length && viewer.current?.hide(selection)}
                onShowAll={() => viewer.current?.showAll()}
                colorMode={colorMode}
                setColorMode={setColorMode}
                section={section}
                setSection={setSection}
                treeOpen={treeOpen}
                toggleTree={() => setTreeOpen((v) => !v)}
                toggleTable={() => navigate(`/projects/${projectId}/panels`)}
                toggleSearch={() => setSearch((v) => !v)}
                statsOpen={statsOpen}
                toggleStats={() => setStatsOpen((v) => !v)}
                labelsOn={labelsOn}
                toggleLabels={() =>
                  setLabelTargets((t) =>
                    t.panels || t.anchors
                      ? { panels: false, anchors: false }
                      : { panels: true, anchors: true }
                  )
                }
                selectionCount={selection.length}
                shifted={showBulkBar}
              />
            ) : null}

            {search ? (
              <div className="absolute left-1/2 top-20 z-20 w-72 -translate-x-1/2">
                <input
                  autoFocus
                  data-testid="viewer-search-input"
                  placeholder="Panel name contains…"
                  value={filters.panel_name || ""}
                  onChange={(e) => setFilters({ ...filters, panel_name: e.target.value })}
                  className="mono w-full rounded-full border border-cyan-500/30 bg-white/95 px-4 py-2 text-xs text-slate-900 backdrop-blur"
                />
              </div>
            ) : null}

            <div className="pointer-events-none absolute bottom-3 left-4 z-20 hidden items-center gap-4 sm:flex">
              {ready ? (
                <span className="mono flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                  <Gauge size={11} /> {panels.filter((p) => !p.is_anchor).length} panels · {manifest?.definitions?.length || 0} block defs
                </span>
              ) : null}
              {measure ? (
                <span
                  data-testid="measure-result"
                  className="mono rounded-full bg-cyan-500/15 px-3 py-1 text-[10px] text-cyan-700"
                >
                  {Math.round(measure).toLocaleString()} mm
                </span>
              ) : null}
            </div>
            {revisionId && statsOpen ? (
              <ViewerStatsPanel
                viewer={viewer}
                loadTelemetry={loadTelemetry}
                onClose={() => setStatsOpen(false)}
              />
            ) : null}

            {revisionId && ready && colorMode !== "original" && colorMode !== "compare" ? (
              <StatusLegend
                department={colorMode}
                panels={panels}
                selectedStatus={filters[`status_${colorMode}`] || ""}
                onPick={selectByStatus}
              />
            ) : null}




            <div className="pointer-events-auto absolute bottom-3 right-4 z-20 flex gap-2">
              {canUpload ? (
                <button
                  data-testid="upload-model-button"
                  onClick={() => setUploadOpen(true)}
                  className="okn-tool flex items-center gap-1.5 rounded-full border border-slate-200 bg-white/95 px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-700 backdrop-blur hover:bg-slate-100"
                >
                  <CloudUpload size={12} /> Revisions
                </button>
              ) : null}
            </div>

            {colorMode === "compare" && compareResult ? (
              <div
                data-testid="compare-legend"
                className="pointer-events-auto absolute right-4 top-[9.5rem] z-20 rounded-xl border border-slate-200 bg-white/95 p-3 shadow-lg backdrop-blur"
              >
                <p className="mono mb-2 text-[10px] uppercase tracking-wider text-slate-500">
                  {compareResult.base.revision_name} → {compareResult.target.revision_name}
                </p>
                {[
                  ["added", "Added"],
                  ["removed", "Removed"],
                  ["renamed", "Renamed"],
                  ["modified", "Modified"],
                ].map(([key, label]) => (
                  <div key={key} className="flex items-center gap-2 py-0.5 text-[11px]">
                    <span
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ backgroundColor: COMPARE_COLORS[key] }}
                    />
                    <span className="text-slate-700">{label}</span>
                    <span className="mono ml-auto text-slate-500">
                      {compareResult.summary[key]}
                    </span>
                  </div>
                ))}
                <button
                  data-testid="clear-compare-button"
                  onClick={() => {
                    setCompareResult(null);
                    setColorMode("design");
                  }}
                  className="mono mt-2 w-full rounded-full border border-slate-200 py-1 text-[9px] uppercase tracking-wider text-slate-600 hover:bg-slate-100"
                >
                  Clear comparison
                </button>
              </div>
            ) : null}

            {sidePanel && (!isCompact || mobileSheet === "panel") ? (
              <PanelProperties
                projectId={projectId}
                panel={sidePanel}
                pallets={pallets}
                onClose={() => {
                  clearSelection();
                }}
                onChanged={() => {
                  refreshPanel();
                  loadSupport();
                }}
                selection={selection}
                onCreateIssue={setIssuePanel}
              />
            ) : null}

            {markerPanel ? (
              <IssueMarkerPopup
                projectId={projectId}
                panelUid={markerPanel}
                onClose={() => setMarkerPanel(null)}
              />
            ) : null}

            {showBulkBar ? (
              <BulkActionsBar
                projectId={projectId}
                selection={selection}
                pallets={pallets}
                panelVersions={panelVersions}
                onClose={() => {
                  setBulkOpen(false);
                  setMobileSheet(null);
                }}
                onOrderCreated={clearSelection}
                onDone={() => {
                  loadPanels();
                  loadSupport();
                  refreshPanel();
                }}
              />
            ) : null}
            {selection.length > 0 && !showBulkBar ? (
              <button
                key={`bulk-${selection.length}`}
                data-testid="open-bulk-panel-button"
                onClick={() => {
                  setBulkOpen(true);
                  if (isCompact) setMobileSheet("bulk");
                }}
                className="okn-pulse-twice mono absolute left-4 z-20 rounded-full border border-cyan-500/50 bg-white px-5 py-2.5 text-[12.5px] font-medium uppercase tracking-wider text-cyan-700 shadow-xl hover:bg-cyan-50 top-[11.5rem] lg:top-[7.5rem]"
              >
                Change status ({selection.length})
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <ModelUploadDialog
        projectId={projectId}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onActivated={reload}
        onShowInViewer={(result) => {
          setCompareResult(result);
          setColorMode("compare");
          toast.success(
            `Comparison shown in 3D — ${result.summary.added} added, ${result.summary.removed} removed, ${result.summary.renamed} renamed`
          );
        }}
      />
      <PresetEditDialog
        projectId={projectId}
        preset={editPreset}
        selection={selection}
        onOpenChange={(open) => !open && setEditPreset(null)}
        onSaved={loadPresets}
      />

      <IssueDialog
        projectId={projectId}
        panel={issuePanel}
        panelUids={issuePanel?.multi ? selection : null}
        open={!!issuePanel}
        onOpenChange={(open) => !open && setIssuePanel(null)}
        onCreated={() => {
          refreshPanel();
          setMarkerNonce((n) => n + 1);
        }}
      />
    </div>
  );
}
